import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown, Percent, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { Aviso, Avatar, Boton, BotonCirculo, Campo, Cargando, EncabezadoPagina, Insignia, Tarjeta } from '../../componentes/ui.tsx';
import { ConfirmarBorrado } from './Productos.tsx';
import {
  AvisoCredencial,
  copiarClave,
  enlaceWhatsApp,
  ModalCliente,
  ModalUsuario,
  type ClienteAdmin,
  type CredencialReset,
  type UsuarioCliente,
} from './Clientes.tsx';

interface Item {
  id: string;
  productoId: string | null;
  referencia: string;
  nombreProducto: string;
  marcaNombre: string;
  cantidad: number;
  costoUnitario: number;
  subtotal: number;
}

interface Pedido {
  id: string;
  codigoExterno: string;
  fecha: string;
  estado: string;
  total: number;
  items: Item[];
}

/** Ficha del almacén: sus pedidos con CRUD completo y descuento general (D38 siempre recalcula). */
export function PaginaAdminCliente() {
  const { clienteId } = useParams({ from: '/admin/clientes/$clienteId' });
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [aviso, setAviso] = useState<string | null>(null);
  const [creandoPedido, setCreandoPedido] = useState(false);
  const [agregandoItemA, setAgregandoItemA] = useState<string | null>(null);
  const [editandoItem, setEditandoItem] = useState<{ pedidoId: string; item: Item } | null>(null);
  const [descuentoDe, setDescuentoDe] = useState<Pedido | null>(null);
  const [borrandoPedido, setBorrandoPedido] = useState<Pedido | null>(null);
  const [editandoPedido, setEditandoPedido] = useState<Pedido | null>(null);
  const [editandoCliente, setEditandoCliente] = useState(false);
  const [modalUsuario, setModalUsuario] = useState<UsuarioCliente | null | 'nuevo'>(null);
  /** Pedidos expandidos; por defecto todos colapsados. */
  const [pedidosAbiertos, setPedidosAbiertos] = useState<Set<string>>(new Set());
  const [credReset, setCredReset] = useState<CredencialReset | null>(null);

  const clientes = useQuery({
    queryKey: ['admin-clientes'],
    queryFn: () => api<{ items: ClienteAdmin[] }>('/admin/clientes?limit=100'),
  });
  const cliente = clientes.data?.items.find((c) => c.id === clienteId) ?? null;

  const pedidos = useQuery({
    queryKey: ['admin-cliente-pedidos', clienteId],
    queryFn: () => api<{ items: Pedido[] }>(`/admin/clientes/${clienteId}/pedidos`),
  });

  async function recargar(mensaje?: string) {
    if (mensaje) setAviso(mensaje);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin-cliente-pedidos', clienteId] }),
      qc.invalidateQueries({ queryKey: ['admin-clientes'] }),
    ]);
  }

  const borrarItem = useMutation({
    mutationFn: ({ pedidoId, itemId }: { pedidoId: string; itemId: string }) =>
      api(`/admin/pedidos/${pedidoId}/items/${itemId}`, { metodo: 'DELETE' }),
    onSuccess: () => void recargar('Ítem eliminado; costos recalculados.'),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const resetearClave = useMutation({
    mutationFn: (usuarioId: string) =>
      api<{ username: string; password: string }>(`/admin/usuarios/${usuarioId}/reset-password`, { metodo: 'POST' }),
    onSuccess: async (cred) => {
      setAviso(null);
      setCredReset({
        username: cred.username,
        password: cred.password,
        telefono: cliente?.contactoTelefono ?? cliente?.telefono ?? null,
        copiada: await copiarClave(cred.password),
      });
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });
  const alternarUsuario = useMutation({
    mutationFn: (u: UsuarioCliente) =>
      api(`/admin/usuarios/${u.id}`, { metodo: 'PATCH', cuerpo: { estado: u.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO' } }),
    onSuccess: () => void recargar(),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });
  const eliminarUsuario = useMutation({
    mutationFn: (id: string) => api(`/admin/usuarios/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => void recargar('Cuenta eliminada.'),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });
  async function entrarComoUsuario(u: UsuarioCliente) {
    try {
      await api(`/admin/usuarios/${u.id}/impersonar`, { metodo: 'POST' });
      window.location.href = '/';
    } catch (e) {
      setAviso(`Error: ${(e as Error).message}`);
    }
  }

  if (clientes.isPending) return <Cargando texto="Cargando almacén…" />;
  if (!cliente) {
    return (
      <main className="mx-auto w-full max-w-[1600px] space-y-4">
        <Aviso>El almacén no existe.</Aviso>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo={cliente.nombreNegocio ?? cliente.razonSocial}
        subtitulo={`RUC ${cliente.ruc}${cliente.ciudad ? ` · ${cliente.ciudad}` : ''}${cliente.region ? ` · ${cliente.region}` : ''} · ${cliente.numProductos} producto(s) visibles`}
        acciones={
          <div className="flex items-center gap-2">
            <Boton variante="contorno" onClick={() => setCreandoPedido(true)}>
              <Plus size={16} /> Pedido
            </Boton>
            <BotonCirculo aria-label="Volver a clientes" className="h-11 w-11" onClick={() => void navegar({ to: '/admin/clientes' })}>
              <ArrowLeft size={18} />
            </BotonCirculo>
          </div>
        }
      />

      {aviso ? <Aviso tono={aviso.startsWith('Error') ? 'error' : 'ok'}>{aviso}</Aviso> : null}
      {credReset ? <AvisoCredencial cred={credReset} alCerrar={() => setCredReset(null)} /> : null}

      {/* Datos del cliente + contacto + cuentas de acceso */}
      <Tarjeta className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3 xl:grid-cols-4">
            {(
              [
                ['Razón social', cliente.razonSocial],
                ['RUC', cliente.ruc],
                ['Ciudad', cliente.ciudad],
                ['Región', cliente.region],
                ['Email', cliente.email],
                ['Teléfono', cliente.telefono],
                ['Categoría', cliente.categoriaNombre ?? 'Sin categoría'],
                ['Tipo', cliente.tipoNombre ?? 'Sin tipo'],
              ] as const
            ).map(([etiqueta, valor]) => (
              <div key={etiqueta}>
                <p className="text-[11px] font-medium uppercase tracking-wide text-suave">{etiqueta}</p>
                <p className="truncate">{valor || '—'}</p>
              </div>
            ))}
          </div>
          <Boton variante="contorno" tamano="sm" onClick={() => setEditandoCliente(true)}>
            Editar
          </Boton>
        </div>

        {/* Contacto: el botón de WhatsApp va pegado a la persona, no al título */}
        <div className="mt-4 border-t border-superficie2 pt-4">
          <p className="text-[11px] font-medium uppercase tracking-wide text-suave">Contacto</p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <p className="text-[15px] font-medium">
              {cliente.contactoNombre ?? cliente.nombreNegocio ?? cliente.razonSocial}
              {cliente.contactoTelefono ?? cliente.telefono ? (
                <span className="ml-2 text-sm text-suave">{cliente.contactoTelefono ?? cliente.telefono}</span>
              ) : null}
            </p>
            {(cliente.contactoTelefono ?? cliente.telefono) ? (
              <a
                href={enlaceWhatsApp(cliente.contactoTelefono ?? cliente.telefono!)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-9 items-center gap-2 rounded-full bg-emerald-500/15 px-4 text-sm font-semibold text-emerald-600"
              >
                Escribir WhatsApp
              </a>
            ) : null}
          </div>
        </div>

        {/* Cuentas de acceso del almacén */}
        <div className="mt-4 border-t border-superficie2 pt-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wide text-suave">
              Cuentas de acceso ({cliente.usuarios.length})
            </p>
            <Boton variante="texto" tamano="sm" onClick={() => setModalUsuario('nuevo')}>
              <Plus size={14} /> Cuenta
            </Boton>
          </div>
          <ul className="divide-y divide-superficie2 text-sm">
            {cliente.usuarios.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <span className="font-mono">{u.username}</span>{' '}
                  <span className="text-xs text-suave">
                    {u.rol === 'CLIENTE_ADMIN' ? 'Admin' : 'Vendedor'}
                    {u.estado !== 'ACTIVO' ? ' · INACTIVO' : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Boton variante="texto" tamano="sm" onClick={() => void entrarComoUsuario(u)}>Entrar</Boton>
                  <Boton variante="texto" tamano="sm" onClick={() => setModalUsuario(u)}>Editar</Boton>
                  <Boton variante="texto" tamano="sm" onClick={() => resetearClave.mutate(u.id)}>Clave nueva</Boton>
                  <Boton variante="texto" tamano="sm" onClick={() => alternarUsuario.mutate(u)}>
                    {u.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
                  </Boton>
                  <Boton variante="texto" tamano="sm" className="!text-rose-600" onClick={() => eliminarUsuario.mutate(u.id)}>
                    Eliminar
                  </Boton>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Tarjeta>

      {pedidos.data?.items.length === 0 ? (
        <Tarjeta className="p-6 text-sm text-suave">
          Sin pedidos. Cree uno con + Pedido o súbalos por Excel en Clientes → Pedidos.
        </Tarjeta>
      ) : null}

      <ul className="space-y-4">
        {pedidos.data?.items.map((p) => (
          <li key={p.id}>
            <Tarjeta className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Toda la cabecera es tocable: expande o colapsa el pedido. */}
                <div
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
                  role="button"
                  tabIndex={0}
                  aria-expanded={pedidosAbiertos.has(p.id)}
                  onClick={() =>
                    setPedidosAbiertos((prev) => {
                      const sig = new Set(prev);
                      if (sig.has(p.id)) sig.delete(p.id);
                      else sig.add(p.id);
                      return sig;
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPedidosAbiertos((prev) => {
                        const sig = new Set(prev);
                        if (sig.has(p.id)) sig.delete(p.id);
                        else sig.add(p.id);
                        return sig;
                      });
                    }
                  }}
                >
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-suave transition-transform ${pedidosAbiertos.has(p.id) ? '' : '-rotate-90'}`}
                  />
                  <Avatar nombre={p.codigoExterno} />
                  <div>
                    <p className="text-[16px] font-medium">
                      {p.codigoExterno}
                      <span className="ml-2 align-middle"><Insignia>{p.estado}</Insignia></span>
                    </p>
                    <p className="text-xs text-suave">
                      {new Date(p.fecha).toLocaleDateString()} · {p.items.length} ítem(s) · total ${p.total.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Boton variante="contorno" tamano="sm" onClick={() => setAgregandoItemA(p.id)}>
                    <Plus size={14} /> Ítem
                  </Boton>
                  <Boton variante="contorno" tamano="sm" onClick={() => setDescuentoDe(p)}>
                    <Percent size={14} /> Descuento
                  </Boton>
                  <Boton variante="contorno" tamano="sm" onClick={() => setEditandoPedido(p)}>
                    Editar
                  </Boton>
                  <Boton variante="peligro" tamano="sm" onClick={() => setBorrandoPedido(p)}>
                    <Trash2 size={14} /> Borrar
                  </Boton>
                </div>
              </div>

              {pedidosAbiertos.has(p.id) && p.items.length > 0 ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-suave">
                      <tr>
                        <th className="px-3 py-2">Código</th>
                        <th className="px-3 py-2">Producto</th>
                        <th className="px-3 py-2">Marca</th>
                        <th className="px-3 py-2 text-right">Cant.</th>
                        <th className="px-3 py-2 text-right">Costo</th>
                        <th className="px-3 py-2 text-right">Subtotal</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-superficie2">
                      {p.items.map((i) => (
                        <tr key={i.id} className="hover:bg-superficie2">
                          <td className="px-3 py-2 font-mono text-xs">{i.referencia}</td>
                          <td className="px-3 py-2">{i.nombreProducto}</td>
                          <td className="px-3 py-2 text-suave">{i.marcaNombre}</td>
                          <td className="px-3 py-2 text-right">{i.cantidad}</td>
                          <td className="px-3 py-2 text-right">${i.costoUnitario.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-medium">${i.subtotal.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Boton variante="contorno" tamano="sm" onClick={() => setEditandoItem({ pedidoId: p.id, item: i })}>
                                Editar
                              </Boton>
                              <Boton
                                variante="peligro"
                                tamano="sm"
                                onClick={() => borrarItem.mutate({ pedidoId: p.id, itemId: i.id })}
                              >
                                Quitar
                              </Boton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : pedidosAbiertos.has(p.id) ? (
                <p className="mt-3 text-sm text-suave">Pedido vacío: agregue ítems con + Ítem.</p>
              ) : null}
            </Tarjeta>
          </li>
        ))}
      </ul>

      {editandoCliente ? (
        <ModalCliente
          cliente={cliente}
          alCerrar={() => setEditandoCliente(false)}
          alListo={async (m) => {
            setEditandoCliente(false);
            await recargar(m ?? 'Cliente actualizado.');
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {modalUsuario !== null ? (
        <ModalUsuario
          cliente={cliente}
          usuario={modalUsuario === 'nuevo' ? null : modalUsuario}
          alCerrar={() => setModalUsuario(null)}
          alListo={async (m) => {
            setModalUsuario(null);
            await recargar(m);
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {creandoPedido ? (
        <ModalPedido
          clienteId={clienteId}
          pedido={null}
          alCerrar={() => setCreandoPedido(false)}
          alListo={async () => {
            setCreandoPedido(false);
            await recargar('Pedido creado.');
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {editandoPedido ? (
        <ModalPedido
          clienteId={clienteId}
          pedido={editandoPedido}
          alCerrar={() => setEditandoPedido(null)}
          alListo={async () => {
            setEditandoPedido(null);
            await recargar('Pedido actualizado.');
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {agregandoItemA ? (
        <ModalItem
          pedidoId={agregandoItemA}
          item={null}
          alCerrar={() => setAgregandoItemA(null)}
          alListo={async () => {
            setAgregandoItemA(null);
            await recargar('Ítem agregado; costos recalculados.');
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {editandoItem ? (
        <ModalItem
          pedidoId={editandoItem.pedidoId}
          item={editandoItem.item}
          alCerrar={() => setEditandoItem(null)}
          alListo={async () => {
            setEditandoItem(null);
            await recargar('Ítem actualizado; costos recalculados.');
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {descuentoDe ? (
        <ModalDescuento
          pedido={descuentoDe}
          alCerrar={() => setDescuentoDe(null)}
          alListo={async (n) => {
            setDescuentoDe(null);
            await recargar(`Descuento aplicado a ${n} ítem(s); los precios del almacén ya usan el costo nuevo.`);
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {borrandoPedido ? (
        <ConfirmarBorrado
          mensaje={`Se borra el pedido ${borrandoPedido.codigoExterno} con sus ${borrandoPedido.items.length} ítem(s); la visibilidad y los costos del almacén se recalculan con los pedidos restantes.`}
          alCancelar={() => setBorrandoPedido(null)}
          alConfirmar={async () => {
            try {
              await api(`/admin/pedidos/${borrandoPedido.id}`, { metodo: 'DELETE' });
              setBorrandoPedido(null);
              await recargar('Pedido borrado; costos recalculados.');
            } catch (e) {
              setBorrandoPedido(null);
              setAviso(`Error: ${(e as Error).message}`);
            }
          }}
        />
      ) : null}
    </main>
  );
}

function ModalPedido({
  clienteId,
  pedido,
  alCerrar,
  alListo,
  alFallar,
}: {
  clienteId: string;
  pedido: Pedido | null;
  alCerrar: () => void;
  alListo: () => Promise<void>;
  alFallar: (m: string) => void;
}) {
  const [codigo, setCodigo] = useState(pedido?.codigoExterno ?? '');
  const [fecha, setFecha] = useState((pedido?.fecha ?? new Date().toISOString()).slice(0, 10));
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo = { codigoExterno: codigo.trim(), fecha: new Date(`${fecha}T12:00:00`).toISOString() };
      if (pedido) await api(`/admin/pedidos/${pedido.id}`, { metodo: 'PATCH', cuerpo });
      else await api(`/admin/clientes/${clienteId}/pedidos`, { metodo: 'POST', cuerpo });
      await alListo();
    } catch (err) {
      alFallar((err as Error).message);
      alCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={guardar} className="tarjeta w-full max-w-sm space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">{pedido ? `Editar ${pedido.codigoExterno}` : 'Nuevo pedido'}</h2>
        <Campo etiqueta="Código del pedido" value={codigo} onChange={(e) => setCodigo(e.target.value)} required autoFocus />
        <Campo etiqueta="Fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
        <p className="text-xs text-suave">La fecha manda: el costo vigente del almacén es el de su compra más reciente.</p>
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="submit" disabled={guardando || !codigo.trim()}>{guardando ? 'Guardando…' : 'Guardar'}</Boton>
        </div>
      </form>
    </div>
  );
}

function ModalItem({
  pedidoId,
  item,
  alCerrar,
  alListo,
  alFallar,
}: {
  pedidoId: string;
  item: Item | null;
  alCerrar: () => void;
  alListo: () => Promise<void>;
  alFallar: (m: string) => void;
}) {
  const [referencia, setReferencia] = useState(item?.referencia ?? '');
  const [cantidad, setCantidad] = useState(String(item?.cantidad ?? 1));
  const [costo, setCosto] = useState(item ? item.costoUnitario.toFixed(2) : '');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      const costoNumero = Math.round(Number(costo.replace(',', '.')) * 100) / 100;
      if (item) {
        await api(`/admin/pedidos/${pedidoId}/items/${item.id}`, {
          metodo: 'PATCH',
          cuerpo: { cantidad: Number(cantidad), costoUnitario: costoNumero },
        });
      } else {
        await api(`/admin/pedidos/${pedidoId}/items`, {
          metodo: 'POST',
          cuerpo: { referencia: referencia.trim(), cantidad: Number(cantidad), costoUnitario: costoNumero },
        });
      }
      await alListo();
    } catch (err) {
      alFallar((err as Error).message);
      alCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={guardar} className="tarjeta w-full max-w-sm space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">{item ? `Editar ${item.referencia}` : 'Agregar ítem'}</h2>
        {!item ? (
          <Campo etiqueta="Código del producto" value={referencia} onChange={(e) => setReferencia(e.target.value)} required autoFocus />
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Cantidad" type="number" min={1} value={cantidad} onChange={(e) => setCantidad(e.target.value)} required />
          <Campo etiqueta="Costo unitario" inputMode="decimal" value={costo} onChange={(e) => setCosto(e.target.value)} required />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="submit" disabled={guardando || (!item && !referencia.trim()) || !costo}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </form>
    </div>
  );
}

function ModalDescuento({
  pedido,
  alCerrar,
  alListo,
  alFallar,
}: {
  pedido: Pedido;
  alCerrar: () => void;
  alListo: (items: number) => Promise<void>;
  alFallar: (m: string) => void;
}) {
  const [porcentaje, setPorcentaje] = useState('');
  const [guardando, setGuardando] = useState(false);
  const p = Number(porcentaje.replace(',', '.'));
  const valido = Number.isFinite(p) && p > 0 && p < 100;
  const totalNuevo = valido ? pedido.total * (1 - p / 100) : null;

  async function aplicar() {
    setGuardando(true);
    try {
      const r = await api<{ itemsActualizados: number }>(`/admin/pedidos/${pedido.id}/descuento`, {
        metodo: 'POST',
        cuerpo: { porcentaje: Math.round(p * 100) / 100 },
      });
      await alListo(r.itemsActualizados);
    } catch (err) {
      alFallar((err as Error).message);
      alCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="tarjeta w-full max-w-sm space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">Descuento a {pedido.codigoExterno}</h2>
        <p className="text-sm text-suave">
          Baja el costo de TODOS los ítems del pedido en el porcentaje indicado. Con eso se
          actualizan los precios a los que se le vendió al almacén (si este pedido es su compra
          más reciente por referencia).
        </p>
        <Campo
          etiqueta="Porcentaje de descuento"
          inputMode="decimal"
          placeholder="10"
          value={porcentaje}
          onChange={(e) => setPorcentaje(e.target.value)}
          autoFocus
        />
        {valido ? (
          <p className="text-sm">
            Total: <span className="line-through opacity-60">${pedido.total.toFixed(2)}</span>{' '}
            <span className="font-medium">${totalNuevo!.toFixed(2)}</span>
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="button" disabled={guardando || !valido} onClick={() => void aplicar()}>
            {guardando ? 'Aplicando…' : 'Aplicar descuento'}
          </Boton>
        </div>
      </div>
    </div>
  );
}
